/**
 * fontLicense/components/LicenseConfirmationModal.tsx
 *
 * Blocking modal that collects license data for every detected font before the
 * upload proceeds. Closing/cancelling resolves the pending Promise with null so
 * the caller aborts the upload; OK validates the data, creates the FontLicense
 * records (POST /laws/font_licenses) and resolves with their ids.
 *
 * By default all detected fonts share one license block: the common case is a
 * whole font pack (typically a single .zip) approved by one legal Jira task. The
 * "Individual licenses" mode switches to a block per font for mixed batches.
 */

import React, { useEffect, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { ExportOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Segmented,
  Select,
  Spin,
  Tooltip,
  Typography,
  message,
} from '@myua/kit';
import { JIRA_TASK_URL_PREFIX, LicenseInfo, LicenseSubmission, LicenseType } from '../model';
import { createFontLicense, getLicenseTypes, validateFontLicense } from '../licenseApi';
import { isJiraUrlPrefixOnly, isValidJiraUrl, normalizeJiraUrl } from '../urlValidation';
import { useLicenseConfirmation } from '../LicenseConfirmationContext';
import { _resolveWithCancel, _resolveWithLicenses } from '../LicenseConfirmationService';

/** Shape of a single license block's form values. */
interface FontFormValue {
  jiraUrl?: string;
  licenseTypeId?: number;
  perpetual?: boolean;
  expiresAt?: Dayjs | null;
}

/** Keyed by FontFileEntry.fileKey, plus SHARED_FIELD_KEY for the shared block. */
type FormValues = Record<string, FontFormValue>;

/**
 * Form key of the shared license block. Cannot collide with a fileKey: those are
 * always `${index}:${sourceFileName}:${archiveEntryName ?? ''}` and therefore
 * always contain a colon.
 */
const SHARED_FIELD_KEY = 'shared';

/** How the batch is licensed: one license for everything, or one per font. */
type LicenseMode = 'shared' | 'individual';

interface LicenseFieldsProps {
  /** Form key the block writes to: a font's fileKey, or SHARED_FIELD_KEY. */
  fieldKey: string;
  /** Heading above the fields (font name, or a summary of the shared block). */
  title: React.ReactNode;
  licenseTypes: LicenseType[];
  form: ReturnType<typeof Form.useForm<FormValues>>[0];
}

const LicenseFields: React.FC<LicenseFieldsProps> = ({ fieldKey, title, licenseTypes, form }) => {
  const perpetual = Form.useWatch([fieldKey, 'perpetual'], form);
  const jiraUrl = Form.useWatch([fieldKey, 'jiraUrl'], form);

  // The "open the task" button appears only once the user has left the field: while
  // typing the number the URL is half-finished, and an icon flickering in mid-word
  // is noise. Blur is also where an emptied field gets its prefix back.
  const [jiraUrlBlurred, setJiraUrlBlurred] = useState(false);
  const onJiraUrlBlur = () => {
    setJiraUrlBlurred(true);
    if (!normalizeJiraUrl(form.getFieldValue([fieldKey, 'jiraUrl']))) {
      form.setFieldValue([fieldKey, 'jiraUrl'], JIRA_TASK_URL_PREFIX);
    }
  };

  const openableUrl = jiraUrlBlurred && isValidJiraUrl(jiraUrl) ? normalizeJiraUrl(jiraUrl) : null;

  return (
    <div style={{ marginBottom: 24 }}>
      {title}

      <Form.Item
        name={[fieldKey, 'jiraUrl']}
        label="Jira task URL"
        initialValue={JIRA_TASK_URL_PREFIX}
        rules={[
          { required: true },
          {
            validator: (_rule, value) => {
              // Prefilled-but-untouched passes `required` (it is not empty), so the
              // missing task number has to be reported here.
              if (isJiraUrlPrefixOnly(value)) {
                return Promise.reject(new Error('Add the Jira task number.'));
              }
              if (!value || isValidJiraUrl(value)) return Promise.resolve();
              return Promise.reject(new Error('Format: https://jira.my.games/browse/AOA-XXXXX'));
            },
          },
          // No uniqueness check: one legal task may cover several fonts, so the very
          // same URL can be reused for other fonts here and for files uploaded earlier.
        ]}
      >
        <Input
          placeholder="https://jira.my.games/browse/AOA-12345"
          allowClear
          onBlur={onJiraUrlBlur}
          // Tabbing into an input selects its whole value, so the first keystroke
          // would wipe the prefix — collapse the caret to the end instead. Mouse
          // clicks keep the browser's own caret placement (it lands after focus).
          onFocus={(event) => {
            const { value } = event.currentTarget;
            event.currentTarget.setSelectionRange(value.length, value.length);
          }}
          suffix={
            openableUrl ? (
              <Tooltip title="Open the task in a new tab">
                <Button
                  type="text"
                  size="small"
                  icon={<ExportOutlined />}
                  href={openableUrl}
                  target="_blank"
                  rel="noreferrer"
                  // Keeps the click from stealing focus back into the input.
                  onMouseDown={(event) => event.preventDefault()}
                />
              </Tooltip>
            ) : (
              // A zero-width placeholder keeps the input width stable when the icon
              // shows up, so the field does not twitch on blur.
              <span style={{ width: 0 }} />
            )
          }
        />
      </Form.Item>

      <Form.Item
        name={[fieldKey, 'licenseTypeId']}
        label="License type"
        rules={[{ required: true }]}
      >
        <Select
          placeholder="Select a license type"
          options={licenseTypes.map((t) => ({ label: t.name, value: t.id }))}
        />
      </Form.Item>

      <Form.Item name={[fieldKey, 'perpetual']} valuePropName="checked" initialValue={false}>
        <Checkbox>Perpetual license (no expiration)</Checkbox>
      </Form.Item>

      <Form.Item
        name={[fieldKey, 'expiresAt']}
        label="Expiration date"
        rules={[
          ({ getFieldValue }) => ({
            validator: (_rule, value) => {
              if (getFieldValue([fieldKey, 'perpetual'])) return Promise.resolve();
              if (!value) return Promise.reject(new Error('Expiration date is required.'));
              return Promise.resolve();
            },
          }),
        ]}
      >
        {/* The license record is created on submit and the backend requires a date
            strictly in the future, so today and earlier are not selectable. */}
        <DatePicker
          disabled={!!perpetual}
          format="YYYY-MM-DD"
          style={{ width: '100%' }}
          disabledDate={(current) => !!current && !current.isAfter(dayjs(), 'day')}
        />
      </Form.Item>
    </div>
  );
};

export const LicenseConfirmationModal: React.FC = () => {
  const { state, dispatch } = useLicenseConfirmation();
  const { open, fonts } = state;

  const [form] = Form.useForm<FormValues>();
  const [licenseTypes, setLicenseTypes] = useState<LicenseType[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<LicenseMode>('shared');

  // A single font has nothing to share with, so the switch would only add noise.
  const modeSwitchVisible = fonts.length > 1;
  const shared = modeSwitchVisible && mode === 'shared';

  // Load license types on open (once per open) and reset the form.
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setMode('shared');
    let cancelled = false;
    setTypesLoading(true);
    getLicenseTypes()
      .then((types) => {
        if (!cancelled) setLicenseTypes(types);
      })
      .catch(() => {
        if (!cancelled) void message.error('Failed to load license types. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setTypesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, form]);

  const onCancel = () => {
    if (submitting) return;
    _resolveWithCancel();
    dispatch({ type: 'CLOSE' });
  };

  /**
   * Switches the mode, carrying the already entered data over so nothing has to be
   * retyped: shared → individual seeds every font block that is still untouched,
   * individual → shared seeds the shared block from the first font. Blocks the user
   * has already filled in are never overwritten.
   */
  const onModeChange = (next: LicenseMode) => {
    const values = form.getFieldsValue();
    const hasTaskNumber = (v?: FontFormValue) => !!v?.jiraUrl && !isJiraUrlPrefixOnly(v.jiraUrl);

    if (next === 'individual') {
      const source = values[SHARED_FIELD_KEY];
      if (hasTaskNumber(source)) {
        fonts.forEach((entry) => {
          if (!hasTaskNumber(values[entry.fileKey])) {
            form.setFieldValue([entry.fileKey], { ...source });
          }
        });
      }
    } else {
      const source = fonts.map((entry) => values[entry.fileKey]).find(hasTaskNumber);
      if (source && !hasTaskNumber(values[SHARED_FIELD_KEY])) {
        form.setFieldValue([SHARED_FIELD_KEY], { ...source });
      }
    }

    setMode(next);
  };

  const onOk = () => {
    if (submitting || typesLoading) return;
    setSubmitting(true);
    form
      .validateFields()
      .then(async (values) => {
        // Only the mounted blocks carry data: one shared block, or one per font.
        const filledKeys = shared ? [SHARED_FIELD_KEY] : fonts.map((entry) => entry.fileKey);

        // Server-side format check (same validator the create endpoint applies).
        const results = await Promise.all(
          filledKeys.map((fieldKey) => validateFontLicense(values[fieldKey]?.jiraUrl ?? '')),
        );
        const invalidFields = filledKeys
          .map((fieldKey, idx) => ({ fieldKey, result: results[idx] }))
          .filter(({ result }) => !result.valid)
          .map(({ fieldKey }) => ({
            name: [fieldKey, 'jiraUrl'] as [string, 'jiraUrl'],
            errors: ['Invalid Jira task URL.'],
          }));
        if (invalidFields.length > 0) {
          form.setFields(invalidFields);
          return;
        }

        // Materialise every collected license as a FontLicense record (created with
        // no node) and hand the resulting ids back to the caller, which binds them
        // to the concrete File (bindLicenseIdsToFiles) for the upload to carry.
        //
        // One record per *font*: every license carries `filename` — the entry inside
        // the archive it covers (null for a font uploaded directly) — so a .zip with
        // five fonts yields five records on the archive node even in shared mode,
        // and the moderation table can show which font each license is about.
        // The dedupe key still guards against creating the same record twice for one
        // font, and the same legal task used for two different files yields a record
        // each — a record can be attached to exactly one node.
        const createdByDedupeKey = new Map<string, number>();
        const submission: LicenseSubmission = {};
        try {
          for (const entry of fonts) {
            const v = values[shared ? SHARED_FIELD_KEY : entry.fileKey] ?? {};
            const info: LicenseInfo = {
              jiraUrl: (v.jiraUrl ?? '').trim(),
              filename: entry.insideArchive ? entry.archiveEntryName ?? null : null,
              licenseTypeId: v.licenseTypeId ?? 0,
              perpetual: !!v.perpetual,
              expiresAt: v.perpetual ? null : v.expiresAt ? v.expiresAt.format('YYYY-MM-DD') : null,
            };
            const dedupeKey = [
              entry.sourceIndex,
              info.jiraUrl,
              info.filename ?? '',
              info.licenseTypeId,
              info.perpetual ? 'perpetual' : info.expiresAt,
            ].join('|');
            let licenseId = createdByDedupeKey.get(dedupeKey);
            if (licenseId === undefined) {
              licenseId = await createFontLicense(info);
              createdByDedupeKey.set(dedupeKey, licenseId);
            }
            submission[entry.fileKey] = { ...info, licenseId };
          }
        } catch {
          // Keep the modal open so the user can retry. Records already created stay
          // unattached — they are excluded from the moderation list, so a retry
          // leaves nothing visible behind.
          void message.error('Failed to save the font licenses. Please try again.');
          return;
        }

        _resolveWithLicenses(submission);
        dispatch({ type: 'CLOSE' });
      })
      .catch(() => {
        // validateFields rejection: field errors are already shown.
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Modal
      open={open}
      title="Font license required"
      okText="Confirm and upload"
      onOk={onOk}
      onCancel={onCancel}
      confirmLoading={submitting}
      okButtonProps={{ disabled: typesLoading }}
      cancelButtonProps={{ disabled: submitting }}
      maskClosable={false}
      width={640}
      // destroyOnClose у antd 5.29 помечен deprecated в пользу destroyOnHidden.
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        message={
          modeSwitchVisible
            ? 'Fonts can only be uploaded with a confirmed license. A font pack is normally approved by one legal task, so by default a single license covers everything detected here — switch to individual licenses if the fonts were approved separately.'
            : 'Fonts can only be uploaded with a confirmed license. Fill in the license data for the detected font.'
        }
        style={{ marginBottom: 16 }}
      />

      {/* Legal's instruction for uploaders. The URL is hardcoded: there is no config
          for such links, so a moved document means a front-end release. */}
      <div style={{ marginBottom: 16 }}>
        <Typography.Link
          href="https://docs.google.com/document/d/1C52x9sQuxn0W7VUR7tgQohtXi0gY1o-unomUas6xqoQ/edit?tab=t.0"
          target="_blank"
          rel="noreferrer"
        >
          How to prepare fonts for uploading <ExportOutlined />
        </Typography.Link>
      </div>

      {modeSwitchVisible && (
        <Segmented<LicenseMode>
          value={mode}
          onChange={onModeChange}
          disabled={submitting}
          options={[
            { label: 'Shared license', value: 'shared' },
            { label: 'Individual licenses', value: 'individual' },
          ]}
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={typesLoading}>
        <Form form={form} layout="vertical" validateMessages={{ required: 'Required field' }}>
          {/* Only the active blocks are mounted: unmounted Form.Items keep their
              values but stop taking part in validation, which is what lets the two
              modes coexist in one form without validating the hidden one. */}
          {shared ? (
            <LicenseFields
              fieldKey={SHARED_FIELD_KEY}
              title={
                <>
                  <Typography.Text strong>
                    One license for all {fonts.length} detected fonts
                  </Typography.Text>
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {fonts.map((entry) => entry.displayName).join(', ')}
                    </Typography.Text>
                  </div>
                </>
              }
              licenseTypes={licenseTypes}
              form={form}
            />
          ) : (
            fonts.map((entry) => (
              <LicenseFields
                key={entry.fileKey}
                fieldKey={entry.fileKey}
                title={<Typography.Text strong>{entry.displayName}</Typography.Text>}
                licenseTypes={licenseTypes}
                form={form}
              />
            ))
          )}
        </Form>
      </Spin>
    </Modal>
  );
};
